import React, { useMemo } from "react";
import {
  Stack,
  Typography,
  TextField,
  Autocomplete,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useGet } from "../../hooks/api";
import { apiUrls } from "../../utils/urls";
import { useProfile } from "../../context/ProfileContext";

/**
 * Shared employee impersonation selector. Renders an Autocomplete of active
 * employees and an optional "Viewing X (read-only)" caption. Use the same
 * value/onChange/onClear across ToDoList and ProgramSchedule so impersonation
 * is shared (e.g. lift state to Dashboard).
 *
 * @param {object|null} value - { profileUuid, label } or null
 * @param {function} onChange - (newValue) => void
 * @param {function} onClear - () => void
 * @param {boolean} [showCaption=true] - Show "Viewing X (read-only)" when impersonating
 * @param {object} [sx] - Extra sx for the outer Stack
 */
export default function EmployeeImpersonationSelect({
  value,
  onChange,
  onClear,
  showCaption = true,
  sx,
}) {
  const { profile } = useProfile();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isAdmin = profile?.is_admin;

  const { data: employeesData } = useGet(apiUrls.admin.employees(), {
    params: {
      search_params: { is_active: true },
      keep_related: true,
      select_related: ["user", "user__profile"],
      values: [
        "uuid",
        "user__first_name",
        "user__last_name",
        "user__profile__uuid",
      ],
    },
    enabled: isAdmin,
  });

  const options = useMemo(() => {
    const employees = employeesData?.data || [];
    return employees
      .map((employee) => {
        const profileUuid =
          employee.user__profile__uuid || employee.user?.profile?.uuid;
        const firstName =
          employee.user__first_name || employee.user?.first_name || "";
        const lastName =
          employee.user__last_name || employee.user?.last_name || "";
        const label = `${firstName} ${lastName}`.trim() || "Unnamed";
        return { profileUuid, label };
      })
      .filter((option) => option.profileUuid)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [employeesData]);

  if (!isAdmin || options.length === 0) return null;

  return (
    <Stack
      direction={isMobile ? "column" : "row"}
      spacing={1}
      alignItems={isMobile ? "stretch" : "center"}
      sx={{
        minWidth: isMobile ? "100%" : 320,
        flexGrow: isMobile ? 1 : 0,
        ...sx,
      }}
    >
      <Autocomplete
        size="small"
        options={options}
        value={value ?? null}
        onChange={(_, newValue) => onChange(newValue)}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(option, val) =>
          option?.profileUuid === val?.profileUuid
        }
        clearOnEscape
        fullWidth
        sx={{ minWidth: isMobile ? "100%" : 260 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={value ? "Impersonating" : "Impersonate"}
            placeholder="Search employees"
            size="small"
            fullWidth
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
        )}
      />
      {value && (
        <Chip
          label="Exit"
          color="warning"
          variant="outlined"
          onDelete={onClear}
          sx={{ fontWeight: 600 }}
          size="small"
        />
      )}
      {showCaption && value && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.75rem",
            my: 0.5,
          }}
        >
          Viewing {value.label} (read-only)
        </Typography>
      )}
    </Stack>
  );
}
